/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-01-05 10:30:50
 *
 *******************************************************/
//@flow
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { AlphaPicker, HuePicker } from 'react-color';
import Classnames from 'classnames';
import { Saturation } from 'react-color/lib/components/common';
import tinycolor2 from 'tinycolor2';
import { TextField } from '@shopify/polaris';
import { RGBAToHex, isHexString } from 'src/helpers/color-helper';

const inlineStyles = {
  saturation: {
    width: '100%',
    paddingBottom: '75%',
    position: 'relative',
    overflow: 'hidden'
  }
};
const CustomPointer = () => {
  return <div className="custom-pointer" />;
};

export const RenderColorCustomPicker = (field: Object) => {
  const { color = '#000', disableAlpha, input } = field;
  const timeout = useRef();
  const previousColorHex = useRef();
  const [hex, setHex] = useState(color);
  const [alpha, setAlpha] = useState(100);
  const [hsl, setHSL] = useState({
    h: 0,
    s: 0,
    l: 0,
    a: 1
  });
  const [hsv, setHSV] = useState({
    h: 0,
    s: 0,
    v: 0
  });
  const [rgb, setRGB] = useState({
    r: 0,
    g: 0,
    b: 0,
    a: 1
  });

  useEffect(() => {
    const initColor = tinycolor2(color);
    const rgb = initColor.toRgb();
    setHex(initColor.toHexString().toUpperCase());
    previousColorHex.current = initColor.toHexString().toUpperCase();

    setHSV(initColor.toHsv());
    setRGB(rgb);
    setHSL(initColor.toHsl());
    setAlpha(_.round(rgb.a * 100));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaturationChange = (hsv: ColorResult) => {
    const color = tinycolor2(hsv);
    const rgb = color.toRgb();
    setHex(color.toHexString().toUpperCase());
    previousColorHex.current = color.toHexString().toUpperCase();
    setHSV(color.toHsv());
    setRGB(rgb);
    input.onChange({
      hex: color.toHexString(),
      rgb: rgb
    });
    if (rgb.r === 0 && rgb.g === 0 && rgb.b === 0) {
      return;
    }
    setHSL(color.toHsl());
  };

  const handleHueChange = colorChange => {
    const changedRGB = colorChange.rgb;
    _.set(changedRGB, 'a', rgb.a);
    const changedHSL = colorChange.hsl;
    _.set(changedHSL, 'a', hsl.a);

    setHSL(changedHSL);
    setRGB(changedRGB);
    setHex(colorChange.hex.toUpperCase());
    previousColorHex.current = colorChange.hex.toUpperCase();
  };

  const handleHueChangeComplete = color => {
    input.onChange({ rgb: color.rgb, hex: RGBAToHex(color.rgb) });
  };

  const handleAlphaChange = colorChange => {
    setHSL(colorChange.hsl);
    setRGB(colorChange.rgb);
    setAlpha(_.round(colorChange.rgb.a * 100));
  };

  const handleAlphaChangeComplete = color => {
    input.onChange({ rgb: color.rgb, hex: RGBAToHex(color.rgb) });
  };

  const handleAlphaInputChange = value => {
    const regexNumber = /^\d+$/;
    if (regexNumber.test(value)) {
      setAlpha(value);
      const regex = /\b([0-9]|[1-9][0-9]|100)\b/g;
      if (regex.test(value)) {
        setHSL({
          ...hsl,
          a: parseFloat(value / 100)
        });
        setRGB({
          ...rgb,
          a: parseFloat(value / 100)
        });

        const rgbaChange = {
          ...rgb,
          a: parseFloat(value / 100)
        };

        input.onChange({
          rgb: rgbaChange,
          hex: RGBAToHex(rgbaChange)
        });
      }
      if (timeout.current) {
        clearTimeout(timeout.current);
      }

      timeout.current = setTimeout(() => {
        validateAlphaInput(value);
      }, 300);
    } else if (value === '') {
      setAlpha(value);
    }
  };

  const validateAlphaInput = value => {
    const regex = /\b([0-9]|[1-9][0-9]|100)\b/g;
    if (!regex.test(value)) {
      setAlpha(100);
      setHSL({
        ...hsl,
        a: 1
      });
      setRGB({
        ...rgb,
        a: 1
      });

      const rgbaChange = {
        ...rgb,
        a: 1
      };
      input.onChange({
        rgb: rgbaChange,
        hex: RGBAToHex(rgbaChange)
      });
    }
  };

  const handleChangeInput = value => {
    setHex(value);

    if (isHexString(value)) {
      const color = tinycolor2(value);
      const rgb = color.toRgb();
      _.set(rgb, 'a', parseFloat(alpha / 100));
      const hsl = color.toHsl();
      _.set(hsl, 'a', parseFloat(alpha / 100));

      setRGB(rgb);
      setHSL(hsl);

      previousColorHex.current = String(value).toUpperCase();
      input.onChange({
        hex: RGBAToHex(rgb),
        rgb: rgb
      });
    }
  };

  const handleBlurTextField = (e, value) => {
    e.preventDefault();
    const regex = /^#([\da-f]{3}){1,2}$/i;
    if (!regex.test(hex)) {
      const color = tinycolor2(previousColorHex.current);
      setHex(color.toHexString().toUpperCase());
      setRGB(color.toRgb());
      setHSL(color.toHsl());
      input.onChange({
        hex: RGBAToHex(color.toRgb()),
        rgb: color.toRgb()
      });
    }
  };

  const handleBlurAlpha = (e, value) => {
    e.preventDefault();
    if (alpha === '') {
      setAlpha(100);
      setHSL({
        ...hsl,
        a: 1
      });
      setRGB({
        ...rgb,
        a: 1
      });

      const rgbaChange = {
        ...rgb,
        a: 1
      };
      input.onChange({
        rgb: rgbaChange,
        hex: RGBAToHex(rgbaChange)
      });
    }
  };

  return (
    <div className="custom-color-picker">
      <div className="custom-color-picker__saturation" style={inlineStyles.saturation}>
        <Saturation hsl={hsl} hsv={hsv} pointer={CustomPointer} onChange={handleSaturationChange} />
      </div>

      <div className="custom-color-picker__hue">
        <HuePicker
          color={hsl}
          onChange={handleHueChange}
          onChangeComplete={handleHueChangeComplete}
        />
      </div>
      {!disableAlpha && (
        <div className="custom-color-picker__alpha">
          <AlphaPicker
            color={hsl}
            onChange={handleAlphaChange}
            onChangeComplete={handleAlphaChangeComplete}
          />
        </div>
      )}

      <div
        className={Classnames('custom-color-picker__input', {
          'custom-color-picker__input--disable-alpha': disableAlpha
        })}>
        <TextField
          value={hex}
          connectedRight={
            !disableAlpha ? (
              <div className="custom-color-picker__input-alpha">
                <TextField
                  value={alpha}
                  type="number"
                  min={0}
                  max={100}
                  onChange={handleAlphaInputChange}
                  connectedRight={<span>%</span>}
                  onBlur={handleBlurAlpha}
                />
              </div>
            ) : null
          }
          onChange={handleChangeInput}
          onBlur={handleBlurTextField}
        />
      </div>
    </div>
  );
};
